import { LitElement, html, unsafeHTML } from '@hh/global.js'

const CACHE_TTL_MS = 60 * 1000

// Module-level in-memory cache shared across every instance on the page.
const memoryCache = new Map()

function normalizeRoot() {
  const root = window.Shopify?.routes?.root || '/'
  return root.endsWith('/') ? root : `${root}/`
}

function readSessionCache(key) {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const { html: cachedHtml, expires } = JSON.parse(raw)
    if (!expires || Date.now() > expires) {
      sessionStorage.removeItem(key)
      return null
    }
    return cachedHtml
  } catch (e) {
    return null
  }
}

function writeSessionCache(key, value) {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({ html: value, expires: Date.now() + CACHE_TTL_MS })
    )
  } catch (e) {
    // Storage may be unavailable / full; caching is best-effort.
  }
}

// Section Rendering API wraps the response in `#shopify-section-{id}`.
// Only the inner HTML is the card; injecting the wrapper duplicates that
// fixed id on every instance.
function unwrapSectionHtml(markup, sectionId) {
  const section = new DOMParser()
    .parseFromString(markup, 'text/html')
    .querySelector(`#shopify-section-${sectionId}`)
  return (section ? section.innerHTML : markup).trim()
}

class DynamicProductCard extends LitElement {
  static properties = {
    handle: { type: String },
    template: { type: String },
    placeholderTitle: { type: String, attribute: 'title' },
    placeholderImage: { type: String, attribute: 'image' },
    _markup: { state: true },
    _failed: { state: true },
  }

  constructor() {
    super()
    this.template = 'product-card'
    this._markup = null
    this._failed = false
  }

  // Render into the light DOM so global product-card.css applies and the
  // inner custom elements (product-card-wrapper, hh-product-form,
  // hh-product-card-variants, hh-swiper) upgrade normally.
  createRenderRoot() {
    return this
  }

  connectedCallback() {
    super.connectedCallback()
    this.fetchCard()
  }

  get cacheKey() {
    return `dpc:${this.template}:${this.handle}`
  }

  async fetchCard() {
    if (!this.handle || this._markup) return

    const cached = memoryCache.get(this.cacheKey) || readSessionCache(this.cacheKey)
    if (cached) {
      const markup = unwrapSectionHtml(cached, this.template)
      memoryCache.set(this.cacheKey, markup)
      this._markup = markup
      return
    }

    try {
      const url = `${normalizeRoot()}products/${this.handle}?section_id=${
        this.template
      }`
      const response = await fetch(url)
      if (!response.ok) throw new Error(`Request failed: ${response.status}`)

      const markup = unwrapSectionHtml(await response.text(), this.template)
      if (!markup) throw new Error('Empty card response')

      memoryCache.set(this.cacheKey, markup)
      writeSessionCache(this.cacheKey, markup)
      this._markup = markup
    } catch (e) {
      console.error(`[dynamic-product-card] ${this.handle}:`, e)
      this._failed = true
      this.removeFromView()
    }
  }

  // Remove the card and its list/carousel wrapper so a failed fetch never leaves
  // a blank grid cell or slide behind.
  removeFromView() {
    const swiperEl = this.closest('hh-swiper')
    const wrapper = this.closest('.swiper-slide, li')
    ;(wrapper || this).remove()
    swiperEl?.swiper?.update()
  }

  renderSkeleton() {
    if (this._failed) return html``
    return html`
      <div class='dynamic-product-card__skeleton' aria-hidden='true'>
        <div class='dynamic-product-card__skeleton-media'>
          ${this.placeholderImage
            ? html`<img
                class='dynamic-product-card__skeleton-image'
                src='${this.placeholderImage}&width=400'
                alt=''
                loading='lazy'
              />`
            : html``}
        </div>
        <div class='dynamic-product-card__skeleton-text'>
          ${this.placeholderTitle
            ? html`<span class='dynamic-product-card__skeleton-title'
                >${this.placeholderTitle}</span
              >`
            : html`<span
                class='dynamic-product-card__skeleton-line dynamic-product-card__skeleton-line--title'
              ></span>`}
          <span
            class='dynamic-product-card__skeleton-line dynamic-product-card__skeleton-line--price'
          ></span>
        </div>
      </div>
    `
  }

  render() {
    return this._markup ? html`${unsafeHTML(this._markup)}` : this.renderSkeleton()
  }
}

customElements.define('dynamic-product-card', DynamicProductCard)
